import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import ToggleButton from "react-bootstrap/ToggleButton"
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup"
import { DateTime } from "luxon"

import useT from "utils/hooks/useT"
import { Popup } from "components"
import CertificateOfAnalysis from "components/Lims/CertificateOfAnalysis"
import StatusBadge from "components/Lims/StatusBadge"
import { openPresignedFile } from "utils/s3Download"
import { limsRelease, limsSampleQc, limsBatchHold, sampleView } from "state_management"

// Batch History (SPEC-064, Plannertech-user#4157) — the READ-ONLY half of QA's world. The customer's
// cut is by ACTION, not by object: everything QA can act on lives in QA Review (the release queue
// and, since this SPEC, the active batch holds with their signed "Lift hold"), and everything that
// merely records what already happened lives here — the released lots with their certificate, and
// the blocked lots with the signature that lifted the block, if one has.
//
// There is deliberately NOT a single mutating button on this screen. R10/R11 (SPEC-055) removed
// duplicate release tables from the old QA Batch Release screen precisely because the same
// QA-signed release could be started from three screens without any of them showing the other two;
// a "lift" shortcut here would rebuild that mistake. The rows below are the same records QA Review
// acts on — shown, not offered.
//
// Both tables answer to ONE filter row: period, product, batch/lot and a status toggle. The blocked
// table lists ALL holds, active ones included: a lot's history that omits "and it is still blocked"
// is misleading, and the toggle's "blocked" would otherwise list only lots that are no longer
// blocked. The action on an active hold still lives only in QA Review.
const STATUS = { ALL: "all", RELEASED: "released", BLOCKED: "blocked" }

// The period bounds are inclusive and compared on the DAY, so a lot released at 15:40 still matches
// a "to" of that same date (#4139: an end-of-day comparison, not a midnight one — that off-by-a-day
// is exactly what makes a date filter feel broken). A record with no date at all is KEPT: those
// predate the stamp, and silently hiding history is worse than a row with an em dash in its date.
function inPeriod(iso, fromDt, toDt) {
  if (!iso) return true
  const at = DateTime.fromISO(iso)
  if (!at.isValid) return true
  if (fromDt && at < fromDt) return false
  if (toDt && at > toDt) return false
  return true
}

// Free-text batch/lot match across every identifier a row carries (the sample/lot name AND the
// batch-lot strings registered on its samples), case-insensitive substring. An empty query matches.
function matchesLot(candidates, needle) {
  if (!needle) return true
  return candidates.some((c) => c && String(c).toLowerCase().includes(needle))
}

// The batch/lot strings registered on a group's sample-analyses (the F-C1 sample-QC `batchLot`) —
// the same join limsRelease uses for the release gate, done here for display and filtering only.
function lotsForGroup(group, sampleQc) {
  const lots = (group.analyses || [])
    .map((a) => (a.sampleId && sampleQc[a.sampleId] ? sampleQc[a.sampleId].batchLot : ""))
    .filter(Boolean)
  return Array.from(new Set(lots))
}

export default function BatchHistory() {
  const { t } = useT("common")
  const dispatch = useDispatch()

  const sampleGroupsRaw = useSelector((s) => s.sampleView.sampleGroups, shallowEqual)
  const sampleGroups = useMemo(() => sampleGroupsRaw || [], [sampleGroupsRaw])
  const relItems = useSelector((s) => s.limsRelease.items, shallowEqual)
  const sampleQc = useSelector((s) => s.limsSampleQc, shallowEqual)
  const holds = useSelector((s) => s.limsBatchHold.items, shallowEqual)
  const members = useSelector((s) => s.members)

  const [status, setStatus] = useState(STATUS.ALL)
  // #4139 kept the released list on a WINDOW because it is append-only and grows forever. This
  // screen is now the only route to that history, so the window opens wider (30 days) — still a
  // window, because the alternative is a page that gets slower every month.
  const [from, setFrom] = useState(() => DateTime.local().minus({ days: 30 }).toISODate())
  const [to, setTo] = useState(() => DateTime.local().toISODate())
  const [product, setProduct] = useState("")
  const [lotQuery, setLotQuery] = useState("")
  const [viewingCert, setViewingCert] = useState(null) // the released group whose printable CoA is open

  useEffect(() => {
    // Read-only screen: the four collections it displays, nothing else. sampleView + sampleQc are
    // needed to join a release back to its analyses (the certificate) and to resolve which product
    // a bare lot string belongs to.
    dispatch(limsRelease.fetch())
    dispatch(sampleView.fetch())
    dispatch(limsSampleQc.fetch())
    dispatch(limsBatchHold.fetch())
  }, [dispatch])

  // A hold record knows only its lot string. The product comes from the sample groups that
  // registered that lot — without it the product filter would silently empty half the screen.
  const productsByLot = useMemo(() => {
    const map = {}
    sampleGroups.forEach((g) => {
      if (!g.productName) return
      const analyses = g.analyses || []
      analyses.forEach((a) => {
        const lot = a.sampleId && sampleQc[a.sampleId] ? sampleQc[a.sampleId].batchLot : ""
        if (!lot) return
        if (!map[lot]) map[lot] = []
        if (!map[lot].includes(g.productName)) map[lot].push(g.productName)
      })
    })
    return map
  }, [sampleGroups, sampleQc])

  // Released rows: one per release record, joined back to its Sample-View group (so the certificate
  // can read the analyses); falls back to the release record's own fields if the group is gone.
  const releasedRows = useMemo(() => {
    const groupByKey = buildGroupByObservation(sampleGroups)
    return relItems.map((rel) => {
      const group = groupByKey[rel.id] || {
        productName: rel.productName,
        sampleName: rel.sampleName,
        observationId: rel.observationId,
        analyses: [],
      }
      return { rel, group, lots: lotsForGroup(group, sampleQc) }
    })
  }, [sampleGroups, relItems, sampleQc])

  const productOptions = useMemo(() => {
    const names = new Set()
    releasedRows.forEach(({ group }) => {
      if (group.productName) names.add(group.productName)
    })
    holds.forEach((h) => (productsByLot[h.batchLot] || []).forEach((p) => names.add(p)))
    return Array.from(names).sort((a, b) => a.localeCompare(b))
  }, [releasedRows, holds, productsByLot])

  const releasedFiltered = useMemo(() => {
    const fromDt = from ? DateTime.fromISO(from).startOf("day") : null
    const toDt = to ? DateTime.fromISO(to).endOf("day") : null
    const needle = lotQuery.trim().toLowerCase()
    return releasedRows.filter(
      ({ rel, group, lots }) =>
        inPeriod(rel.releasedAt, fromDt, toDt) &&
        (!product || group.productName === product) &&
        matchesLot([group.sampleName, ...lots], needle)
    )
  }, [releasedRows, from, to, product, lotQuery])

  // Blocked rows are filtered on the date the hold was PLACED — that is the event the history is
  // about; a lift is shown on the row, not used to date it.
  const blockedFiltered = useMemo(() => {
    const fromDt = from ? DateTime.fromISO(from).startOf("day") : null
    const toDt = to ? DateTime.fromISO(to).endOf("day") : null
    const needle = lotQuery.trim().toLowerCase()
    return holds
      .map((hold) => ({ hold, products: productsByLot[hold.batchLot] || [] }))
      .filter(
        ({ hold, products }) =>
          inPeriod(hold.rejectedAt, fromDt, toDt) &&
          (!product || products.includes(product)) &&
          matchesLot([hold.batchLot], needle)
      )
  }, [holds, productsByLot, from, to, product, lotQuery])

  const initials = (id) => (id && members[id] && members[id].initials) || "—"
  const fmtDate = (iso) => (iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy HH:mm") : "—")

  // Download the released lot's certificate: prefer the SERVER-generated certificate (presigned GET
  // from the qaReports area); fall back to the client-rendered printable CoA when none was stored
  // (the qaReports bucket was not provisioned at release time). Reading, not releasing — which is
  // why it is the one button this screen has, and why the `coa` redirect now lands here.
  const downloadCertificate = (rel, group) => {
    if (limsRelease.hasStoredCertificate(rel)) {
      dispatch(limsRelease.certificateDownloadUrl(rel))
        .then((url) => {
          if (url) openPresignedFile(url)
          else setViewingCert(group)
        })
        .catch(() => setViewingCert(group))
      return
    }
    setViewingCert(group)
  }

  return (
    <div className="LimsQueue">
      <h1 className="LimsQueue-title">{t("limsBatchHistory")}</h1>
      <p className="LimsQueue-intro">{t("limsBatchHistoryIntro")}</p>

      {/* One filter row for BOTH tables: period, product, batch/lot, and the status toggle whose
          options are the section names themselves, so the toggle reads as "show me this section". */}
      <div className="LimsQueue-filters">
        <span className="LimsQueue-filters-label">{t("limsBatchHistoryPeriod")}</span>
        <label>
          {t("from")}
          <Form.Control type="date" size="sm" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          {t("to")}
          <Form.Control type="date" size="sm" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          {t("limsCoaProduct")}
          <Form.Select size="sm" value={product} onChange={(e) => setProduct(e.target.value)}>
            <option value="">{t("limsBatchHistoryAllProducts")}</option>
            {productOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Form.Select>
        </label>
        <label>
          {t("limsBatchHoldLot")}
          <Form.Control
            type="search"
            size="sm"
            value={lotQuery}
            placeholder={t("limsBatchHistoryLotPlaceholder")}
            onChange={(e) => setLotQuery(e.target.value)}
          />
        </label>
        <ToggleButtonGroup
          type="radio"
          name="batch-history-status"
          className="LimsQueue-filters-status"
          value={status}
          onChange={setStatus}
        >
          <ToggleButton id="batch-history-status-all" value={STATUS.ALL} variant="light" size="sm">
            {t("limsBatchHistoryStatusAll")}
          </ToggleButton>
          <ToggleButton id="batch-history-status-released" value={STATUS.RELEASED} variant="light" size="sm">
            {t("limsReleasedHeading")}
          </ToggleButton>
          <ToggleButton id="batch-history-status-blocked" value={STATUS.BLOCKED} variant="light" size="sm">
            {t("limsBatchHoldsHeading")}
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {status !== STATUS.BLOCKED && (
        <section className="LimsQueue-section">
          <h2 className="LimsQueue-subtitle">{t("limsReleasedHeading")}</h2>
          {releasedFiltered.length === 0 ? (
            <p className="LimsQueue-empty">
              {releasedRows.length === 0 ? t("limsReleasedEmpty") : t("limsBatchHistoryNoMatch")}
            </p>
          ) : (
            <Table striped hover responsive className="LimsQueue-table">
              <thead>
                <tr>
                  <th>{t("limsCoaProduct")}</th>
                  <th>{t("limsCoaSample")}</th>
                  <th>{t("limsCoaSampleId")}</th>
                  <th>{t("limsCoaReleasedAt")}</th>
                  <th>{t("limsCoaReleasedBy")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {releasedFiltered.map(({ rel, group }) => (
                  <tr key={rel.id}>
                    <td>{group.productName || t("none")}</td>
                    <td>{group.sampleName || "—"}</td>
                    <td>{group.observationId != null && group.observationId !== "" ? group.observationId : "—"}</td>
                    <td>{fmtDate(rel.releasedAt)}</td>
                    <td>{initials(rel.releasedBy)}</td>
                    <td className="LimsQueue-actions">
                      <div className="LimsQueue-actions-inner">
                        <Button variant="outline-secondary" size="sm" onClick={() => downloadCertificate(rel, group)}>
                          {t("limsSampleQaDownloadCertificate")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      {status !== STATUS.RELEASED && (
        <section className="LimsQueue-section">
          <h2 className="LimsQueue-subtitle">{t("limsBatchHoldsHeading")}</h2>
          {blockedFiltered.length === 0 ? (
            <p className="LimsQueue-empty">
              {holds.length === 0 ? t("limsBatchHoldsEmpty") : t("limsBatchHistoryNoMatch")}
            </p>
          ) : (
            <Table striped hover responsive className="LimsQueue-table">
              <thead>
                <tr>
                  <th>{t("limsCoaProduct")}</th>
                  <th>{t("limsBatchHoldLot")}</th>
                  <th>{t("limsBatchHoldRejectedBy")}</th>
                  <th>{t("limsBatchHoldRejectedAt")}</th>
                  <th>{t("limsBatchHoldReason")}</th>
                  <th>{t("limsBatchHoldStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {blockedFiltered.map(({ hold, products }) => (
                  <tr key={hold.id} className={hold.liftedAt ? "is-lifted" : undefined}>
                    <td>{products.length > 0 ? products.join(", ") : "—"}</td>
                    <td>{hold.batchLot || "—"}</td>
                    <td>{initials(hold.rejectedBy)}</td>
                    <td>{fmtDate(hold.rejectedAt)}</td>
                    <td>{hold.reason || "—"}</td>
                    <td>
                      {hold.liftedAt ? (
                        <>
                          <StatusBadge
                            variant="neutral"
                            title={t("limsBatchHoldLiftedDetail", {
                              by: initials(hold.liftedBy),
                              at: fmtDate(hold.liftedAt),
                              reason: hold.liftReason || "—",
                            })}
                          >
                            {t("limsBatchHoldStatusLifted")}
                          </StatusBadge>
                          {/* The lift signature stays on the row: a lifted hold is a GMP CORRECTION,
                              so the chain (rejected -> lifted, by whom, when, why) must be readable
                              without opening the audit trail. */}
                          <div className="LimsQueue-lift-detail">
                            {t("limsBatchHoldLiftedBy", { by: initials(hold.liftedBy), at: fmtDate(hold.liftedAt) })}
                            {hold.liftReason ? ` — ${hold.liftReason}` : ""}
                          </div>
                        </>
                      ) : (
                        <StatusBadge variant="fail">{t("limsBatchHoldStatusActive")}</StatusBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      {viewingCert && (
        <Popup title={t("limsCoaTitle")} onCancel={() => setViewingCert(null)}>
          <CertificateOfAnalysis group={viewingCert} />
        </Popup>
      )}
    </div>
  )
}

// Index the Sample-View groups by the release key (productName|sampleName|observationId), each
// carrying only that observation's analyses — so a released record (keyed by the compound id) can
// be joined back to its group for the certificate. First write per key wins (stable). Moved here
// with the Released table (SPEC-064); it has no other caller.
function buildGroupByObservation(sampleGroups) {
  const byKey = {}
  ;(sampleGroups || []).forEach((g) => {
    const observations = new Set((g.analyses || []).map((a) => String(a.observationId ?? g.observationId ?? "")))
    observations.forEach((obs) => {
      const key = `${g.productName || ""}|${g.sampleName || ""}|${obs}`
      if (!byKey[key]) {
        byKey[key] = {
          ...g,
          observationId: obs,
          analyses: (g.analyses || []).filter((x) => String(x.observationId ?? g.observationId ?? "") === obs),
        }
      }
    })
  })
  return byKey
}
